import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getExternalDeployment,
  sendExternalConfirmation,
  submitExternalProof,
  verifyExternalProof,
} from "../services/externalDeploymentService";
import { getApiErrorMessage, showApiErrorToast } from "../utils/apiError";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

function statusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "verified") return "bg-emerald-100 text-emerald-800";
  if (["submitted", "proof_submitted"].includes(key)) return "bg-amber-100 text-amber-800";
  if (["rejected", "cancelled"].includes(key)) return "bg-rose-100 text-rose-800";
  return "bg-slate-100 text-slate-700";
}

export default function ExternalDeploymentDetailPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [savingProof, setSavingProof] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [proofForm, setProofForm] = useState({
    completionNote: "",
    quantity: "",
    hours: "",
    unit: "",
  });

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const item = await getExternalDeployment(id);
      setData(item);
    } catch (err) {
      setError(getApiErrorMessage(err, "Externe inzet laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSubmitProof(event) {
    event.preventDefault();
    if (!id) return;
    try {
      setSavingProof(true);
      await submitExternalProof(id, {
        completionNote: proofForm.completionNote,
        quantity: proofForm.quantity ? Number(proofForm.quantity) : undefined,
        hours: proofForm.hours ? Number(proofForm.hours) : undefined,
        unit: proofForm.unit || undefined,
        attachments: [{ fileId: "manual-proof", name: "Handmatig bewijsrecord" }],
      });
      setProofForm({
        completionNote: "",
        quantity: "",
        hours: "",
        unit: "",
      });
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Proof indienen mislukt" });
    } finally {
      setSavingProof(false);
    }
  }

  async function handleVerify(proofId, decision) {
    if (!id || !proofId) return;
    try {
      await verifyExternalProof(id, proofId, {
        decision,
        note: decision === "verified" ? "Akkoord" : "Aanvulling nodig",
      });
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Proof verificatie mislukt" });
    }
  }

  async function handleSendConfirmation() {
    if (!id) return;
    try {
      await sendExternalConfirmation(id, {});
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Bevestiging versturen mislukt" });
    }
  }

  if (loading) return <p>Externe inzet laden...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-slate-600">Geen data gevonden.</p>;

  const proofs = Array.isArray(data.proofs) ? data.proofs : [];

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{data.title || "Externe inzet"}</h1>
            <p className="text-sm text-slate-600">{data.scope || "-"}</p>
            <p className="mt-1 text-xs text-slate-500">
              Project: {String(data.projectId || "-")} · Model: {data.engagementModel || "-"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusTone(data.status)}`}>
              {data.status || "-"}
            </span>
            <Link to="/pl/externe-inzet" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              Terug
            </Link>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Proof registreren</h2>
          <button
            type="button"
            onClick={handleSendConfirmation}
            className="rounded border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800"
          >
            Bevestiging mailen
          </button>
        </div>
        <form onSubmit={handleSubmitProof} className="grid gap-3 md:grid-cols-4">
          <input
            value={proofForm.completionNote}
            onChange={(e) => setProofForm((prev) => ({ ...prev, completionNote: e.target.value }))}
            placeholder="Toelichting gereedmelding"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
            required
          />
          <input
            value={proofForm.quantity}
            onChange={(e) => setProofForm((prev) => ({ ...prev, quantity: e.target.value }))}
            placeholder="Hoeveelheid (optioneel)"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            value={proofForm.hours}
            onChange={(e) => setProofForm((prev) => ({ ...prev, hours: e.target.value }))}
            placeholder="Uren (optioneel)"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            value={proofForm.unit}
            onChange={(e) => setProofForm((prev) => ({ ...prev, unit: e.target.value }))}
            placeholder="Eenheid"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="md:col-span-3" />
          <button
            type="submit"
            disabled={savingProof}
            className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {savingProof ? "Opslaan..." : "Proof indienen"}
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Proof records</h2>
        {proofs.length === 0 ? (
          <p className="text-sm text-slate-500">Nog geen proof records.</p>
        ) : (
          <div className="space-y-2">
            {proofs.map((proof) => (
              <article key={proof._id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900">{proof.completionNote || "-"}</p>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusTone(proof.status)}`}>
                    {proof.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {formatDateTime(proof.performedAt)} · qty: {proof.quantity ?? "-"} · hours: {proof.hours ?? "-"}
                </p>
                <div className="mt-2 flex gap-2">
                  {proof.status !== "verified" ? (
                    <button
                      type="button"
                      onClick={() => handleVerify(proof._id, "verified")}
                      className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800"
                    >
                      Verifiëren
                    </button>
                  ) : null}
                  {proof.status !== "rejected" ? (
                    <button
                      type="button"
                      onClick={() => handleVerify(proof._id, "rejected")}
                      className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-xs text-rose-800"
                    >
                      Afkeuren
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
